import { useMutation } from "@tanstack/react-query"
import { TrendingUp } from "lucide-react"
import { useId, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"

/**
 * Registrar un avance: qué se hizo, y opcionalmente por dónde va.
 *
 * ⚠ **El porcentaje se deja en blanco a propósito.** «Llamé y no contestan» es un
 * avance de verdad y no se deja medir; obligar a un número haría que se pusiera
 * cualquiera, y un número inventado es peor que ninguno.
 *
 * ⚠ **Un avance sobre una tarea pendiente la arranca**, y eso se dice aquí antes de
 * pulsar. Que el estado cambie solo sin avisar es de las cosas que hacen desconfiar
 * de una pantalla.
 */

type Props = {
  tareaId: number
  /** Para decir de antemano que esto va a mover la tarea a «en curso». */
  pendiente: boolean
  onRegistrado: () => void
}

export function AvanceNuevo({ tareaId, pendiente, onRegistrado }: Props) {
  const idBase = useId()
  const [nota, setNota] = useState("")
  const [porcentaje, setPorcentaje] = useState("")

  const registrar = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/tareas/${tareaId}/avances`, {
        nota: nota.trim(),
        // Cadena vacía y «cero por ciento» son cosas distintas: el servidor
        // espera `null` para «no lo mido».
        porcentaje: porcentaje === "" ? null : Number(porcentaje),
      }),
    onSuccess: () => {
      setNota("")
      setPorcentaje("")
      onRegistrado()
    },
  })

  const listo = nota.trim() !== ""

  return (
    <form
      // Sin `aria-label`: repetiría el nombre del campo de dentro y una consulta por
      // «Registrar un avance» encontraría dos elementos.
      className="mt-3"
      onSubmit={(e) => {
        e.preventDefault()
        if (listo) registrar.mutate()
      }}
    >
      <label className="block text-sm" htmlFor={`${idBase}-nota`}>
        Registrar un avance
        <textarea
          id={`${idBase}-nota`}
          rows={2}
          className="campo mt-1"
          placeholder="Llamé, quedaron en mandar la factura mañana"
          value={nota}
          onChange={(e) => setNota(e.target.value)}
        />
      </label>

      <div className="mt-2 flex items-end gap-2">
        <label className="block flex-1 text-sm" htmlFor={`${idBase}-pct`}>
          <span style={{ color: "var(--fg-muted)" }}>Avance %</span>
          <input
            id={`${idBase}-pct`}
            type="number"
            min={0}
            max={100}
            className="campo mt-1 tabular-nums"
            value={porcentaje}
            onChange={(e) => setPorcentaje(e.target.value)}
          />
        </label>
        <button
          type="submit"
          disabled={!listo || registrar.isPending}
          className="boton-primario flex h-9 items-center gap-2 px-3 text-sm disabled:opacity-50"
        >
          <TrendingUp size={14} aria-hidden={true} />
          {registrar.isPending ? "Guardando…" : "Registrar"}
        </button>
      </div>

      {pendiente && (
        <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
          Al registrar el primer avance, la tarea pasa a «en curso».
        </p>
      )}

      {registrar.isError && (
        <p role="alert" className="mt-2 text-sm" style={{ color: "var(--destructive)" }}>
          {registrar.error instanceof ErrorApi
            ? registrar.error.message
            : "No se pudo registrar el avance."}
        </p>
      )}
    </form>
  )
}
